import store, { RootState } from "../../dataManagement/uiDb/UiDb";
import { notificationReceived } from "../../dataManagement/uiDb/NotificationsDb";
import {
  appendLiveMessage,
  incrementYetToRespond,
} from "../../dataManagement/uiDb/MessagesDb";
import MyMessagesThunk from "../thunks/MyMessagesThunk";
import { MessageModal } from "../../interfaces/MessageObject";
import Messageshelper from "../helpers/MessagesHelper";
import genericUtility from "../utility/GenericUtility";
import { routeForNotification } from "../helpers/notificationRouter";
import { showToast } from "../notifications/toastBus";
import { NotificationType } from "../../constants/Enums";
import { handleDineInPush } from "./dineInPushRouter";

export interface PushPayload {
  notification?: { title?: string; body?: string } | null;
  data?: Record<string, string> | null;
}

// Debounce timer for reconciling the unread badge with the authoritative count.
let countReconcileTimer: ReturnType<typeof setTimeout> | null = null;

// Guards against parallel full message reloads when several pushes for unknown
// conversations arrive in quick succession.
let messagesRefetchInFlight = false;

/**
 * Single, framework-agnostic handler for an incoming push — used by BOTH the
 * browser foreground listener (useFcmForeground) and the native Android bridge
 * (window.NineFindsNative.onPushMessage). Updates the relevant store(s) from
 * one push:
 *  - NotificationsDb + in-app toast (the alert — fires for every push)
 *  - MessagesDb live thread + "yet to respond" badge (for chat messages)
 *  - DineInDb, silently, via dineInPushRouter (for DINE_IN_* events)
 *
 * Works outside React via the Redux store singleton and the toast bus, so the
 * native bridge can call it directly.
 */
export function handleIncomingPush(payload: PushPayload): void {
  const data = (payload.data ?? {}) as Record<string, string>;
  // eslint-disable-next-line no-console
  console.log("[push] received:", { notification: payload.notification, data });
  // Native forwards only the data block, so fall back to data fields for the
  // toast text when there's no notification block.
  const title = payload.notification?.title ?? data.senderName ?? "NineFinds";
  const body = payload.notification?.body ?? data.messageContent ?? "";

  const isChatMessage = !!(
    data.messageId &&
    data.conversationId &&
    data.messageContent
  );
  const type = data.type ?? (isChatMessage ? "new_message" : "generic");
  const url = data.deepLink || data.url || routeForNotification(type, data);

  // 1) Alert: notifications slice + in-app toast.
  store.dispatch(
    notificationReceived({ type, title, body, data: { ...data, url } })
  );
  showToast(body ? `${title}: ${body}` : title, NotificationType.INFO);

  // 2) Data: live-update the relevant slice.
  if (type.startsWith("DINE_IN_")) {
    handleDineInPush(data);
    return; // never a chat message — nothing below applies.
  }
  if (!isChatMessage) return;

  const currentUserId = genericUtility.getCookie("userId") || "";
  const iAmReceiver = data.receiverId === currentUserId;

  const message: MessageModal = {
    messageId: data.messageId,
    messageContent: data.messageContent,
    timestamp: data.createdAt || new Date().toISOString(),
    senderId: data.senderId || "",
    receiverId: data.receiverId || "",
    receiverName: data.receiverName || "",
    senderName: data.senderName || "",
    messageTemplate: data.messageTemplate || "",
  };

  const state = store.getState() as RootState;
  // MessagesDb.data is loosely typed in the slice (initial value null), so the
  // conversation shape is read through a local any.
  const convos: any = (state.messages as any).data;
  const conversation =
    convos &&
    [convos.activeConversations, convos.inActiveConversations]
      .flatMap((list: any[]) => list ?? [])
      .find((c: any) => c.offerId === data.conversationId);

  if (conversation) {
    const alreadyExists = conversation.messages?.some(
      (m: MessageModal) => m.messageId === message.messageId
    );
    // Count once per conversation: only bump when this message NEWLY flips the
    // conversation to my turn.
    const wasMyTurn = Messageshelper.isMyturn(conversation, currentUserId);

    store.dispatch(appendLiveMessage({ offerId: data.conversationId, message }));

    if (iAmReceiver && !alreadyExists && !wasMyTurn) {
      store.dispatch(incrementYetToRespond());
    }
  } else if (!messagesRefetchInFlight) {
    // Conversation not loaded — pull it in via REST so it still shows. Guarded
    // so a burst of unknown-conversation pushes doesn't fire parallel reloads.
    messagesRefetchInFlight = true;
    Promise.resolve(store.dispatch(MyMessagesThunk.fetchMyMessages())).finally(
      () => {
        messagesRefetchInFlight = false;
      }
    );
  }

  // Reconcile the badge with the authoritative backend count, debounced.
  if (iAmReceiver) {
    if (countReconcileTimer) clearTimeout(countReconcileTimer);
    countReconcileTimer = setTimeout(() => {
      store.dispatch(MyMessagesThunk.fetchYetToRespondCount());
      countReconcileTimer = null;
    }, 1200);
  }
}
